import { z } from 'zod';
/**
 * Applicability binding: memory/skill ↔ workflow node (DEV-16815/16816).
 * Node-level scoping is finer-grained than the agent-level `AgentMemoryBinding`
 * / `SkillBinding` — it lets a specific node in a workflow graph (e.g. one
 * `llm-agent` node) see a memory/skill that other nodes in the same workflow
 * don't, without binding it to every agent that could ever run that node.
 */
/**
 * Keyed by (workflowId, nodeKey) — the STABLE logical-node identity —
 * rather than WorkflowNode.id, because WorkflowNode rows are copy-on-write:
 * every version cut (patchNodeConfig, patchEdge, full `update`) inserts
 * all-new WorkflowNode rows with new ids. An FK to WorkflowNode.id would
 * silently orphan every binding on the next edit. Mirrors the Prisma model.
 */
export const WorkflowNodeMemoryBindingSchema = z.object({
    workflowId: z.string(),
    nodeKey: z.string(),
    memoryId: z.string(),
    orgId: z.string(),
    createdAt: z.date(),
});
export const WorkflowNodeSkillBindingSchema = z.object({
    workflowId: z.string(),
    nodeKey: z.string(),
    skillId: z.string(),
    orgId: z.string(),
    createdAt: z.date(),
});
/**
 * A selectable workflow LLM-node target for the memory/skill scope pickers
 * (DEV-17157). One row per `llm-agent` node in each of the org's CURRENT
 * workflow versions — the catalog `GET /v1/workflows/bindable-nodes` returns,
 * so the console can offer node targets without an N+1 over workflow details.
 */
export const BindableWorkflowNodeSchema = z.object({
    workflowId: z.string(),
    workflowName: z.string(),
    nodeKey: z.string(),
});
export const CreateWorkflowNodeMemoryBindingInputSchema = z.object({
    memoryId: z.string().min(1),
});
export const CreateWorkflowNodeSkillBindingInputSchema = z.object({
    skillId: z.string().min(1),
});
/**
 * Body for `POST .../nodes/:nodeKey/rename-bindings` — migrates a node's
 * memory/skill bindings to its new key after an in-editor rename (the
 * bindings tables are keyed by nodeKey, which the console lets an operator
 * change before saving).
 */
export const RenameWorkflowNodeBindingsInputSchema = z.object({
    newNodeKey: z.string().min(1),
});
//# sourceMappingURL=workflow-node-binding.js.map